import {
  getBillingAccountAlias,
  joinParts,
} from "@/components/Portal/Documents/BillingAccounts/billingAccountUtils";
import { Country, SCHWEIZ } from "@/constants/countries";
import {
  Address,
  BasicCustomerData,
  CreateBillingAccountRequest,
  DrAccount,
} from "@/services/model";
import { getCountryFromId } from "@/utils/countries";

export type BillingAccountFormValues = {
  alias: string;
  firstName: string;
  lastName: string;
  companyName: string;
  additionalAddress: string;
  street: string;
  houseNumber: string;
  zip: string;
  city: string;
  country: Country;
  email: string;
  reference: string;
  note: string;
};

/** Backend / paste often leave padding that breaks Nr. and PLZ regexes. */
const trimValue = (value?: string | null) => value?.trim() ?? "";

const getAddressValues = (address?: Address) => ({
  additionalAddress: trimValue(address?.postalDeliveryNotes),
  street: trimValue(address?.street),
  houseNumber: trimValue(address?.houseNumber),
  zip: trimValue(address?.zip),
  city: trimValue(address?.city),
  country:
    address?.country?.id == null
      ? SCHWEIZ
      : (getCountryFromId(address.country.id) ?? SCHWEIZ),
});

/** A new Vertragskonto starts with the customer's master data. */
export const getCustomerFormValues = (
  customer?: BasicCustomerData,
): BillingAccountFormValues => {
  const firstName = trimValue(customer?.firstName);
  const lastName = trimValue(customer?.lastName);
  const companyName = trimValue(customer?.companyName);
  const fullName = joinParts(firstName, lastName);

  return {
    alias: companyName || fullName,
    firstName,
    lastName,
    companyName,
    ...getAddressValues(customer?.mainAddress),
    email: "",
    reference: "",
    note: "",
  };
};

export const getAccountFormValues = (
  account: DrAccount,
): BillingAccountFormValues => ({
  alias: getBillingAccountAlias(account).trim(),
  firstName: trimValue(account.billToAddress?.firstName),
  lastName: trimValue(account.billToAddress?.lastName),
  companyName: trimValue(account.billToAddress?.companyName),
  ...getAddressValues(account.billToAddress),
  email: trimValue(account.billingEmail),
  reference: trimValue(account.billingReference),
  note: trimValue(account.billingNotes),
});

const optional = (value: string) => value.trim() || undefined;

/** Create and update share the same body. */
export const toBillingAccountRequest = (
  values: BillingAccountFormValues,
): CreateBillingAccountRequest => ({
  billingAccountAlias: optional(values.alias),
  firstName: optional(values.firstName),
  lastName: optional(values.lastName),
  companyName: optional(values.companyName),
  billingAdditionalAddressDetail: optional(values.additionalAddress),
  street: optional(values.street),
  houseNumber: optional(values.houseNumber),
  zip: optional(values.zip),
  city: optional(values.city),
  country: { id: values.country.ID },
  billingEmail: values.email.trim(),
  billingReference: values.reference.trim(),
  billingNote: optional(values.note),
});
